import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { AccountService } from '../../login/account.service';
import { Account } from '../../login/account.model';
import { CustomerService } from '../../shared/service/customer-service';
import { CreditService } from '../../shared/service/credit-service';
import { WarehouseService } from '../../shared/service/warehouse-service';
import { IWarehouse } from '../../shared/model/warehouse.model';
import { environment } from '../../../../environment.prod';
import { ROLE_BUYER, ROLE_OWNER } from '../../core/config/role.constants';
import { ItemService } from '../../entities/item/item-list/item.service';
import { IDiscount } from '../../entities/item/item-list/item.model';
import { ConfirmWarehouseDialog } from './confirm-warehouse-dialog/confirm-warehouse-dialog';

@Component({
    selector: 'app-user-profile',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './user-profile.component.html',
    styleUrl: './user-profile.component.css'
})
export class UserProfileComponent implements OnInit {
    user = signal<Account | null>(null);
    customer = signal<any>(null);
    creditTerms = signal<any>(null);
    cupoDisponible = signal<number | null>(null);
    discounts = signal<IDiscount[]>([]);
    buyerWarehouses = signal<IWarehouse[]>([]);
    selectedWarehouseCode = signal<string | null>(null);
    showSupportOptions: boolean = false;
    isOwner: boolean = false;
    isBuyer: boolean = false;
    activeTab: string = 'PROFILE';

    constructor(
        private accountService: AccountService,
        private customerService: CustomerService,
        private creditService: CreditService,
        private warehouseService: WarehouseService,
        private itemService: ItemService,
        private dialog: MatDialog
    ) {
        this.user = this.accountService.userIdentity;
    }

    ngOnInit(): void {
        const addressNumber = this.user()?.user.addressNumber;

        this.isOwner = this.accountService.hasAnyAuthority(ROLE_OWNER);
        this.isBuyer = this.accountService.hasAnyAuthority(ROLE_BUYER);

        if (this.isBuyer) {
            this.selectedWarehouseCode.set(this.accountService.selectedWarehouseCode());
            if (addressNumber) {
                this.loadBuyerWarehouses(addressNumber);
            }
        }

        if (addressNumber) {
            this.loadCustomerData(addressNumber);
            this.loadCreditTerms(addressNumber);
            this.loadCupoDisponible(addressNumber);
            this.loadDiscounts(addressNumber);
        }
    }

    loadCustomerData(an: number) {
        this.customerService.getCustomerDataByANs([an]).subscribe({
            next: (res) => {
                const body = res.body as any;
                const customers = body?.customers || body?.customer || [];
                if (customers.length > 0) {
                    this.customer.set(customers[0]);
                    this.loadDiscounts(customers[0].id);
                }
            },
            error: (err) => console.error('Error loading customer', err)
        });
    }

    loadDiscounts(customerId: number) {
        if (!customerId) return;
        this.itemService.getDiscounts(customerId).subscribe({
            next: (res) => {
                const data = res?.body ?? res;
                const discountsList: IDiscount[] = Array.isArray(data) ? data : (data?.discounts || data?.items || [data]);
                this.discounts.set(discountsList);
            },
            error: (err) => console.error('Error loading discounts', err)
        });
    }

    loadCreditTerms(an: number) {
        this.customerService.getCreditTermsByAddressNumber(an).subscribe({
            next: (res) => {
                if (res.body && res.body.customer) {
                    this.creditTerms.set(res.body.customer);
                }
            },
            error: (err) => console.error('Error loading credit terms', err)
        });
    }

    loadCupoDisponible(customerId: number) {
        this.creditService.getCredit(customerId).subscribe({
            next: (res) => {
                if (res.body) {
                    this.cupoDisponible.set(res.body.cupoDisponible);
                }
            },
            error: (err) => console.error('Error loading cupo disponible', err)
        });
    }

    loadBuyerWarehouses(an: number) {
        const warehouseCodes = this.accountService.warehouseCodes();
        this.warehouseService.findByCustomerAn(an).subscribe({
            next: (res) => {
                const responseBody = res.body as any;
                const allWarehouses: IWarehouse[] = responseBody?.warehouses || [];
                const filtered = allWarehouses.filter(w => warehouseCodes.includes(w.code ?? ''));
                this.buyerWarehouses.set(filtered);
            },
            error: (err) => console.error('Error loading buyer warehouses', err)
        });
    }

    selectWarehouse(code: string) {
        if (this.isSelected(code)) return;

        const currentCode = this.selectedWarehouseCode() ?? '';
        const currentWarehouse = this.buyerWarehouses().find(w => w.code === currentCode);
        const newWarehouse = this.buyerWarehouses().find(w => w.code === code);

        this.dialog.open(ConfirmWarehouseDialog, {
            width: '450px',
            maxWidth: '90vw',
            data: {
                currentWarehouseCode: currentCode,
                currentWarehouseName: currentWarehouse?.name ?? '',
                newWarehouseCode: code,
                newWarehouseName: newWarehouse?.name ?? ''
            },
            panelClass: 'lexend-dialog',
            disableClose: true
        }).afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.accountService.updateSelectedWarehouse(code);
                this.selectedWarehouseCode.set(code);
            }
        });
    }

    isSelected(code: string): boolean {
        return this.selectedWarehouseCode() === code;
    }

    facturacion() {
        window.open(environment.facturasUrl, '_blank');
        this.showSupportOptions = false;
    }

    getDisplayRole(): string {
        const roles = this.user()?.user?.roles?.map((r: any) => r.code) || [];
        if (roles.includes(ROLE_OWNER)) return ROLE_OWNER;
        if (roles.includes(ROLE_BUYER)) return ROLE_BUYER;
        return roles[0] || '';
    }

    switchTab(tab: string) {
        this.activeTab = tab;
    }

}
